/** @jsxImportSource @emotion/react */
import { css, Theme, useTheme } from '@emotion/react';
import {
	Dispatch,
	FC,
	SetStateAction,
	useCallback,
	useEffect,
	useState,
} from 'react';
import { useField } from 'formik';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { addDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Box, Flex } from 'components/styled';
import Button from 'components/styled/Button';
import Text from 'components/styled/Text';
import { Wrapper } from 'components/styled/Wrapper';

import { getDateString } from 'utils';

import { MonthsLabels, OfferMaximumValidity } from 'utils/enumMaps';
import i18n, {
	useErrorTranslation,
	useLocalizedMultistring,
} from 'utils/localization';

import ErrorFeedback from '../error/ErrorFeedback';
import SelectInput from '../select/SelectInput';

import { Input, InputWrapper, Label, TextInputProps } from './TextInput';

import 'react-datepicker/dist/react-datepicker.css';

const datePickerStyle = (theme: Theme) => css`
	.react-datepicker {
		border-radius: 0;
	}
	.react-datepicker-popper {
		z-index: 2;
	}
	.react-datepicker__day--today {
		color: ${theme.colors.secondary};
		background: none;
	}
	.react-datepicker__day--selected,
	.react-datepicker__day--today--selected {
		background-color: ${theme.colors.primary};
		color: #fff;
		&:hover {
			background-color: ${theme.colors.primary};
			color: #fff;
		}
	}
	.react-datepicker__day--keyboard-selected {
		color: initial;
		background-color: #fff;
	}
	.react-datepicker__tab-loop {
		display: inline-block;
	}
`;

type DatePickerProps = {
	onSetValue: (id: string, val: Date | [Date, Date] | null) => void;
	colorVariant?: 'inverted' | 'normal';
	label: string;
};
/**TODO: finish styling of DatePicker component */
export const DatePicker: FC<
	Partial<ReactDatePickerProps> & DatePickerProps
> = ({ name, onSetValue, value, label, colorVariant, ...p }) => (
	<InputWrapper
		borderless
		inverted={colorVariant === 'inverted'}
		css={datePickerStyle}
	>
		<ReactDatePicker
			dateFormat="dd/MM/yyyy"
			selected={(value && new Date(value)) || null}
			onChange={val => onSetValue(name ?? '', val)}
			placeholderText={label}
			customInput={<Input />}
			css={css`
				color: ${colorVariant === 'inverted' && 'white'};
				::placeholder {
					opacity: 1;
				}
			`}
			{...p}
		/>
		<Box
			minWidth="auto"
			ml={1}
			aria-label="open options"
			color={colorVariant === 'inverted' ? 'white' : 'text'}
		>
			<MdKeyboardArrowDown size={23} />
		</Box>
	</InputWrapper>
);

export const OffersDatePicker: FC<
	Partial<ReactDatePickerProps> &
		DatePickerProps & {
			isNewOffer?: boolean;
		}
> = ({ name, label, value, onSetValue, maxDate, isNewOffer }) => {
	const { t } = useTranslation('components/form/input/offersdatepicker/');
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const { university } = useTheme();
	return (
		<Box css={datePickerStyle} display="inline-block">
			<Text as="span">{t('until')} </Text>
			<ReactDatePicker
				dateFormat="dd/MM/yyyy"
				selected={(value && new Date(value)) || null}
				onChange={val => {
					if (val !== null && !Array.isArray(val)) {
						onSetValue(name ?? '', val);
					}
					setIsOpen(!isOpen);
				}}
				placeholderText={label}
				customInput={<Input />}
				maxDate={
					maxDate ?? addDays(new Date(), OfferMaximumValidity[university])
				}
				minDate={addDays(new Date(), 1)}
				css={(theme: Theme) => css`
					::placeholder {
						opacity: 1;
					}
					:disabled {
						color: ${theme.colors.text};
					}
				`}
				open={isOpen}
				disabled
			>
				<Box
					px={2}
					maxWidth="230px"
					textAlign="center"
					css={css`
						clear: both;
					`}
				>
					<Text color="error">
						{isNewOffer
							? t('info_new', { days: OfferMaximumValidity[university] })
							: t('info', { days: OfferMaximumValidity[university] })}
					</Text>
				</Box>
			</ReactDatePicker>
			<Button variant="primary" onClick={() => setIsOpen(!isOpen)}>
				{label}
			</Button>
		</Box>
	);
};

export const OfferActivationModalDatePicker: FC<{
	validTo: string | null;
	setValidTo: Dispatch<SetStateAction<string>>;
	activate: () => Promise<void>;
	closeModal: () => void;
}> = ({ validTo, setValidTo, activate, closeModal }) => {
	const { t } = useTranslation(
		'components/form/input/offerActivationModalDatePicker/',
	);
	const { university } = useTheme();
	const maxDate = new Date(
		new Date().setDate(new Date().getDate() + OfferMaximumValidity[university]),
	);
	const minDate = new Date(new Date().setDate(new Date().getDate() + 1));
	const selected = new Date(validTo ?? maxDate);

	return (
		<Wrapper
			css={(theme: Theme) => css`
				${datePickerStyle(theme)}
				.react-datepicker {
					@media (min-width: ${theme.breakpoints[0]}) {
						width: ${theme.breakpoints[0]};
						font-size: ${theme.fontSizes.md}px;
					}
				}
				.react-datepicker__month-container {
					float: none;
					width: 100%;
				}
			`}
		>
			<ReactDatePicker
				withPortal
				selected={selected}
				onChange={(date: Date) => setValidTo(date.toISOString())}
				maxDate={maxDate}
				minDate={minDate}
				inline
			>
				<Box
					textAlign="center"
					minWidth={250}
					css={css`
						clear: both;
					`}
				>
					<Text fontSize="md">{t('title')}</Text>

					<Flex justifyContent="space-around" mb={3}>
						<Text color="textLight">
							{t('from')}: {getDateString(new Date(), i18n.language)}
						</Text>
						<Text fontWeight="bold">
							{t('until')}: {getDateString(selected, i18n.language)}
						</Text>
					</Flex>
					<Text color="error" mb={3} mx={2}>
						{t('info', { days: OfferMaximumValidity[university] })}
					</Text>
					<Flex justifyContent="space-between" mb={2} mx={2}>
						<Button
							onClick={() => {
								closeModal();
							}}
						>
							{t('back')}
						</Button>
						<Button
							onClick={() => {
								activate();
								closeModal();
							}}
						>
							{t('activate')}
						</Button>
					</Flex>
				</Box>
			</ReactDatePicker>
		</Wrapper>
	);
};

type Props = {
	value: Date;
	onSetValue: (id: string, val: string | null) => void;
	onSetTouched: (id: string, val: boolean) => void;
} & Omit<
	TextInputProps,
	| 'value'
	| 'iconLeft'
	| 'iconRight'
	| 'loading'
	| 'placeholder'
	| 'onChange'
	| 'onBlur'
>;

const options = [...Array(12).keys()].map(k => k + 1);

const ledingZeroes = (val: string, zeroes: number) =>
	`${Array(Math.max(0, zeroes - val.length))
		.fill('0')
		.join('')}${val}`;

const DateInput: FC<Props> = ({
	name,
	value,
	label,
	required,
	onSetValue,
	onSetTouched,
	colorVariant,
	...inputProps
}) => {
	const localize = useLocalizedMultistring();
	const { t } = useTranslation('components/form/input/dateinput/');
	const tError = useErrorTranslation();

	const [field, meta] = useField(name);

	const [val, setVal] = useState<{ month: number | null; year: string }>({
		month: null,
		year: '',
	});

	useEffect(() => {
		if (!value) {
			return;
		}
		setVal({
			month: Number.parseInt(
				((value as unknown) as string).match(/(\d\d)-\d\d$/)?.[1] ?? '0',
			),
			year: new Date(value).getFullYear().toString(),
		});
	}, [value]);

	useEffect(() => {
		onSetValue(
			name,
			// eslint-disable-next-line no-nested-ternary
			val.year && val.month !== null
				? `${ledingZeroes(val.year, 4)}-${ledingZeroes(
						val.month.toString(),
						2,
				  )}-01`
				: meta.touched
				? null
				: meta.initialValue,
		);
	}, [name, val, onSetValue, meta.touched, meta.initialValue]);

	const onSetMonth = useCallback((_: string, month: number | null) => {
		setVal(prev => ({ ...prev, month }));
	}, []);

	const nameFromOption = useCallback(
		(m: number | null) =>
			m !== null ? localize(MonthsLabels[m - 1]) ?? '' : '',
		[localize],
	);

	return (
		<Flex
			width={1}
			flexDirection="column"
			color={colorVariant === 'inverted' ? 'white' : 'text'}
		>
			{label && (
				<Label htmlFor={`${name}-month`} required={required}>
					{label}:
				</Label>
			)}
			<Flex gap={3} onClick={() => onSetTouched(name, true)}>
				{/* Month */}
				<SelectInput
					id={`${name}-month`}
					value={val.month as number}
					placeholder={t('month')}
					onSetValue={onSetMonth}
					onSetTouched={onSetTouched}
					options={options}
					unsorted
					nameFromOption={nameFromOption}
					touched={meta.touched}
					// Pass empty error message since it's already shown by current component
					error={meta.error ? ' ' : undefined}
				/>
				{/* Year */}
				<InputWrapper
					hasError={!!meta.error && meta.touched}
					inverted={colorVariant === 'inverted'}
					backgroundColor="white"
				>
					<Input
						{...field}
						name={`${name}-year`}
						value={val.year}
						type="number"
						placeholder={t('year')}
						onChange={e =>
							setVal(prev => ({ ...prev, year: e.target.value.slice(0, 4) }))
						}
						onBlur={() => onSetTouched(name, true)}
						min={1990}
						max={new Date().getFullYear()}
						{...inputProps}
					/>
				</InputWrapper>
			</Flex>

			{meta.error && meta.touched && (
				<ErrorFeedback>{tError(meta.error)}</ErrorFeedback>
			)}
		</Flex>
	);
};
export default DateInput;
